/**
 * Template T2 — "Centered Title + Subline"
 * Clean, centered card with brand colors. Works without hero image.
 */

import React from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { getPriceCopy } from '@/lib/events/priceCopy';
import type { ShareCardProps } from './ShareCardBase';
import { FARMLY_COLORS, FARMLY_FONTS, FarmlyBadge } from './ShareCardBase';

export function TemplateT2({ target, format: fmt, spec }: ShareCardProps) {
  const { w, h } = spec;
  const isStory = fmt === 'ig-story';
  const titleSize = isStory ? 52 : 44;
  const subSize = isStory ? 26 : 24;
  const isShop = target.type === 'shop';
  const isProducer = target.type === 'producer';

  let subline = '';
  let supportingLine = '';
  if (target.type === 'activity') {
    const parts: string[] = [];
    if (target.startDate) {
      try {
        parts.push(format(new Date(target.startDate), "d MMMM yyyy", { locale: sv }));
      } catch { /* */ }
    }
    const price = getPriceCopy({ is_free: target.isFree, price_ore: target.priceOre });
    if (price.primary) parts.push(price.primary);
    subline = parts.join(' · ');
  } else if (target.type === 'product') {
    subline = (target.priceOre / 100).toLocaleString('sv-SE') + ' kr';
  } else if (target.type === 'shop') {
    supportingLine = target.shopIntro
      || 'Utvalda produkter från gården och närområdet';
    if (target.openingSnippet) subline = `🕐 ${target.openingSnippet}`;
  } else if (target.type === 'producer') {
    supportingLine = target.tagline || 'Upptäck vår gård på Farmly';
    if (target.placeName) subline = `📍 ${target.placeName}`;
  }

  return (
    <div
      style={{
        position: 'relative',
        width: w,
        height: h,
        overflow: 'hidden',
        background: (isShop || isProducer)
          ? `linear-gradient(145deg, ${FARMLY_COLORS.deepForest} 0%, hsl(135, 18%, 28%) 50%, hsl(28, 15%, 30%) 100%)`
          : FARMLY_COLORS.deepForest,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 64,
        fontFamily: FARMLY_FONTS.body,
      }}
    >
      {/* Subtle circle accent */}
      <div
        style={{
          position: 'absolute',
          top: isStory ? '15%' : '10%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: Math.min(w, h) * 0.6,
          height: Math.min(w, h) * 0.6,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.04)',
        }}
      />

      {/* Producer tag */}
      <div
        style={{
          fontSize: subSize - 4,
          color: FARMLY_COLORS.warmSand,
          opacity: 0.7,
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontWeight: 500,
          marginBottom: 20,
          zIndex: 1,
        }}
      >
        {target.producerName}
      </div>

      {/* Title */}
      <div
        style={{
          fontFamily: FARMLY_FONTS.display,
          fontSize: titleSize,
          lineHeight: 1.2,
          color: FARMLY_COLORS.warmSand,
          maxWidth: w - 128,
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          zIndex: 1,
        }}
      >
        {target.title}
      </div>

      {/* Supporting line (shop intro) */}
      {supportingLine && (
        <div
          style={{
            marginTop: 16,
            fontSize: subSize - 4,
            lineHeight: 1.5,
            color: FARMLY_COLORS.warmSand,
            opacity: 0.7,
            maxWidth: w - 160,
            zIndex: 1,
          }}
        >
          {supportingLine}
        </div>
      )}

      {/* Subline */}
      {subline && (
        <div
          style={{
            marginTop: supportingLine ? 16 : 24,
            fontSize: subSize,
            color: FARMLY_COLORS.warmSand,
            opacity: 0.8,
            zIndex: 1,
          }}
        >
          {subline}
        </div>
      )}

      {/* Decorative line */}
      <div
        style={{
          marginTop: 32,
          width: 60,
          height: 3,
          background: FARMLY_COLORS.accent,
          borderRadius: 2,
          zIndex: 1,
        }}
      />

      <FarmlyBadge style={{ bottom: 40 }} />
    </div>
  );
}
